import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Close } from "@mui/icons-material";
import { IconButton, Button } from "@mui/material";
import api from "src/store/features/api";
import { useAcceptTrEducationModalMutation } from "src/store/features/api/generated";
import { useFeatureFlag } from "src/utils/hooks/useFeatureFlag";
import { useTrEnabled } from "src/utils/hooks/useTrEnabled";
import usePermission from "src/utils/hooks/usePermission";
import { trackEvent } from "src/Amplitude";

/**
 * D2 — Travel-Rule education modal.
 *
 * Mirrors the `<WelcomeModal />` one-time-server-flag pattern. Opens when:
 *   - the user is not a guest, and
 *   - `userRes.acceptTrEducationModal === false`, and
 *   - the `TR_EDUCATION_MODAL` feature flag is on for the user's entity.
 *
 * On dismiss we PATCH `/account/acceptTrEducationModal` so the modal does not
 * return on the next login or on a different device.
 */
// `UserRes` from generated.ts is a `type` alias, not an `interface`, so it
// can't be augmented via declaration merging. Cast at the call site until
// the OpenAPI generator picks up `acceptTrEducationModal` (added on the
// backend in apps/backend/src/app/user/interfaces/user.interface.ts).
type UserResWithTrFlag = { acceptTrEducationModal?: boolean };

const TrEducationModal: React.FC = () => {
  const { data: userRes } = api.useGetUserDatav2Query();
  const [acceptTrEducationModal] = useAcceptTrEducationModalMutation();
  const { isGuest } = usePermission();
  const { enabled: flagEnabled } = useFeatureFlag("TR_EDUCATION_MODAL");
  // Master TR kill-switch — even if TR_EDUCATION_MODAL is on, suppress the
  // modal when the outbound surface as a whole is off. Avoids teaching users
  // about a feature that the backend will refuse to serve.
  const trOn = useTrEnabled("OUTBOUND");

  const acceptedAlready = (userRes as UserResWithTrFlag | undefined)?.acceptTrEducationModal ?? true;

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const closeModal = async () => {
    try {
      // RTK Query mutation triggers always *resolve* with `{ data }` or
      // `{ error }` — `.unwrap()` rethrows the error so the catch fires.
      await acceptTrEducationModal().unwrap();
    } catch {
      // best-effort — don't block close on a transient API failure
    }
    trackEvent("TR Education Modal Closed");
    setModalIsOpen(false);
  };

  useEffect(() => {
    if (!isGuest && !acceptedAlready && flagEnabled && trOn) {
      trackEvent("TR Education Modal Viewed");
      setModalIsOpen(true);
    }
  }, [acceptedAlready, flagEnabled, isGuest, trOn]);

  return (
    <div>
      <Modal
        open={modalIsOpen}
        onClose={closeModal}
        aria-labelledby="tr-education-modal-title"
        aria-describedby="tr-education-modal-description">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4.5,
            zIndex: "inherit",
            maxWidth: 520,
            width: "90%",
          }}>
          <div style={{ display: "flex", justifyContent: "right" }}>
            <IconButton onClick={closeModal} aria-label="Close">
              <Close />
            </IconButton>
          </div>
          <h2 style={{ margin: 0 }} id="tr-education-modal-title">
            New crypto transfer requirements
          </h2>
          <p id="tr-education-modal-description">
            To comply with financial regulations, we&apos;re required to collect
            some additional information about your crypto transfers, including
            the recipient and whether the destination wallet is yours or a
            third party&apos;s.
          </p>
          <p>
            You&apos;ll see a few extra questions next time you add an address
            or make a withdrawal. This is a one-time update for each address
            and helps us keep your account compliant.
          </p>
          <p>If you have any questions, please reach out to your broker.</p>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" onClick={closeModal} data-testid="tr-education-modal-cta">
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default TrEducationModal;
